import React, { useEffect, useState } from "react";
import "./TrendingTechSection.scss";
import ContentSection from "../components/ContentSection";
import SectionHeader from "../components/SectionHeader";
import BarChart from "../components/BarChart";

const TrendingTechSection: React.FC = () => {
  const [chartWidth, setChartWidth] = useState(700);
  const [chartLeftMargin, setChartLeftMargin] = useState(150);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setChartWidth(300);
        setChartLeftMargin(120);
      } else if (width <= 768) {
        setChartWidth(500);
        setChartLeftMargin(140);
      } else {
        setChartWidth(700);
        setChartLeftMargin(150);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <ContentSection className="trending-tech-section">
      <div className="trending-tech-content">
        <SectionHeader
          title="Trending Tech"
          description="The field is adapting to Generative AI at varying speed."
        />
        <p>
          The use of generative AI has grown exponentially over the past several
          years. In November 2022, OpenAI launched ChatGPT and reached one
          million users in five days (
          <a
            href="https://openai.com/global-affairs/new-economic-analysis/"
            target="_blank"
            rel="noopener noreferrer"
          >
            source
          </a>
          ). As of August 2025, ChatGPT has 700 million weekly active users, and
          many other generative AI platforms have emerged. The use of AI has
          permeated across industries, workforce reentry included.
        </p>
        <p>
          Survey results indicate that AI adoption within the reentry workforce
          sector is growing. When asked about AI implementation, 35% of
          respondents reported no current use of AI, while 32% are utilizing AI
          for administrative tasks. Another 30% indicated they are actively
          exploring potential applications, and 18% are using AI for data
          analysis or reporting purposes. A smaller proportion, 6%, reported
          intentionally avoiding AI at this time.
        </p>
        <p>
          {" "}
          These figures suggest a field that is cautiously integrating AI into
          operations and balancing experimentation with considerations related
          to capacity, organizational readiness, and ethical implications.
        </p>

        <div className="chart-header">
          <h4 className="chart-title">
            AI adoption within the reentry workforce sector
          </h4>
          <p className="chart-subtitle">
            How organizations are currently approaching AI implementation
          </p>
        </div>
        <BarChart
          data={[
            { type: "Not currently using", avg: 35 },
            { type: "Administrative tasks", avg: 32 },
            { type: "Actively exploring potential applications", avg: 30 },
            { type: "Data analysis or reporting", avg: 18 },
            { type: "Intentionally avoiding", avg: 6 },
          ]}
          maxAvg={40}
          width={chartWidth}
          height={350}
          leftMargin={chartLeftMargin}
          colors={["#24B09A", "#84c141", "#ff8c42", "#636466", "#F9D378"]}
          barLabel={(d) => `${d.avg}%`}
          className="responsive-chart"
        />

        <p>
          The broader digital picture underscores why usage is mixed. When asked
          about new tech adopted in the past year, organizations most often
          cited virtual service-delivery tools like Zoom/Teams (41%), along with
          case-management platforms (27%), learning management systems (24%),
          data dashboards (16%), and AI (25%), while 22% adopted no new
          technology at all.
        </p>

        <div className="chart-container">
          <div className="chart-header">
            <h4 className="chart-title">Technology adopted (past year)</h4>
            <p className="chart-subtitle">
              New technologies implemented by organizations
            </p>
          </div>
          <BarChart
            data={[
              { type: "Virtual service delivery tools (Zoom/Teams)", avg: 41 },
              { type: "Case-management platforms", avg: 27 },
              { type: "Artificial Intelligence (AI)", avg: 25 },
              { type: "Learning management systems", avg: 24 },
              { type: "No new technology", avg: 22 },
              { type: "Data dashboards", avg: 16 },
            ]}
            maxAvg={50}
            width={chartWidth}
            height={420}
            leftMargin={chartLeftMargin + 50} // Slightly more for longer labels
            colors={[
              "#24B09A",
              "#84c141",
              "#ff8c42",
              "#636466",
              "#F9D378",
              "#3B7B67",
            ]}
            barLabel={(d) => `${d.avg}%`}
            className="responsive-chart"
          />
        </div>

        <p>
          {" "}
          Top barriers to tech improvements were lack of funding/resources (68%)
          and limited staff capacity/expertise (47%), followed by technology
          infrastructure limitations (23%), competing priorities (23%), and
          data-privacy/security concerns (16%).
        </p>

        <div className="chart-container">
          <div className="chart-header">
            <h4 className="chart-title">Technology barriers</h4>
            <p className="chart-subtitle">
              Top challenges preventing technology improvements
            </p>
          </div>
          <BarChart
            data={[
              { type: "Lack of funding/resources", avg: 68 },
              { type: "Limited staff capacity/expertise", avg: 47 },
              { type: "Technology infrastructure limitations", avg: 23 },
              { type: "Competing priorities", avg: 23 },
              { type: "Data privacy/security concerns", avg: 16 },
            ]}
            maxAvg={80}
            width={chartWidth}
            height={350}
            leftMargin={chartLeftMargin + 50} // Slightly more for longer labels
            colors={["#24B09A", "#84c141", "#ff8c42", "#636466", "#F9D378"]}
            barLabel={(d) => `${d.avg}%`}
            className="responsive-chart"
          />
        </div>

        <p>
          Together, these findings suggest that digital capacity varies widely
          across the field, and that AI adoption may likely remain uneven as
          organizations weigh costs, infrastructure needs, and competing
          priorities.
        </p>
      </div>
    </ContentSection>
  );
};

export default TrendingTechSection;
